import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  DataGrid,
  GridRowModes,
  GridActionsCellItem,
  GridColDef,
  GridRowModesModel,
  GridRowEditStopReasons,
  GridRenderEditCellParams,
  GridEventListener
} from '@mui/x-data-grid';
import {
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Autocomplete,
  Chip,
  Tooltip,
  MenuItem
} from '@mui/material';
import { apiUrl } from '../utils/api';
import { formatCurrency } from '../utils/numberFormatter';
import { dataGridSx } from '../utils/dataGridStyles';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

const QSTeam = ({ projectId, projects = [], access = 'none', userId }) => {
  const readOnly = access !== 'write';

  // DC States
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowModesModel, setRowModesModel] = useState({});
  const [deleteDCDialogOpen, setDeleteDCDialogOpen] = useState(false);
  const [dcToDelete, setDcToDelete] = useState(null);
  const [deletingDC, setDeletingDC] = useState(false);

  // DC Details Dialog (DC info + BOQ inside one popup)
  const [dcDialogOpen, setDcDialogOpen] = useState(false);
  const [savingDC, setSavingDC] = useState(false);
  const [dcFormData, setDcFormData] = useState({
    response_required_by: '',
    cost_impact: '',
    qs_status: 'Pending',
    qs_remarks: '',
    cn_number: '',
    approved_vo: ''
  });

  // BOQ States
  const [selectedDC, setSelectedDC] = useState(null);
  const [dcBoqLines, setDcBoqLines] = useState<any[]>([]);
  const [loadingBoq, setLoadingBoq] = useState(false);
  const [boqRowModesModel, setBoqRowModesModel] = useState<GridRowModesModel>({});
  // Controlled selection for the BOQ grid so a clicked line is visibly
  // highlighted (uncontrolled click-selection wasn't showing the highlight).
  const [selectedBoqRowId, setSelectedBoqRowId] = useState<any>(null);
  // Same idea for the main Design Change grid — track the row a user clicked
  // so it stays highlighted whether or not the details dialog is open.
  const [selectedDCRowId, setSelectedDCRowId] = useState<any>(null);
  const [budgetLineItems, setBudgetLineItems] = useState<any[]>([]);
  const [loadingBudgetItems, setLoadingBudgetItems] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [boqLineToDelete, setBoqLineToDelete] = useState(null);
  const [deletingBoq, setDeletingBoq] = useState(false);
  const [companyData, setCompanyData] = useState({ number_format: '1,234.56' });
  // Tracks BLI selections made inside edit-cell Autocomplete so non-editable derived
  // fields (bli_id, category_name, unit_code) survive the DataGrid's edit-commit cycle.
  const pendingBliRef = useRef<Record<string, any>>({});
  // Live qty/unit_price during inline edit, so the Line Total cell can reflect
  // staged values on every keystroke (DataGrid only commits to row data on save).
  const [liveBoqEdits, setLiveBoqEdits] = useState<Record<string, { qty?: number; unit_price?: number }>>({});

  const fetchData = useCallback(() => {
    if (access === 'none' || !projectId) {
      setRows([]);
      return;
    }
    setLoading(true);
    fetch(apiUrl(`/api/design-changes?project_id=${projectId}`))
      .then(r => r.json())
      .then(changes => {
        const data = changes.map(c => ({ id: c.change_number, ...c }));
        setRows(data);
      })
      .catch(err => {
        console.error('Failed to fetch design changes:', err);
        setRows([]);
      })
      .finally(() => setLoading(false));
  }, [projectId, projects, access]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch company data for number formatting
  useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        const projectRes = await fetch(apiUrl(`/api/projects/${projectId}`), { credentials: 'include' });
        if (!projectRes.ok) return;
        const currentProject = await projectRes.json();
        if (!currentProject.company_id) return;

        const response = await fetch(apiUrl(`/api/companies/${currentProject.company_id}`), { credentials: 'include' });
        if (!response.ok) return;
        const data = await response.json();
        setCompanyData({ number_format: data.number_format || '1,234.56' });
      } catch (error) {
        console.error('Error fetching company data:', error);
      }
    };
    if (projectId) fetchCompanyData();
  }, [projectId]);

  // Fetch DC BOQ
  const fetchDCBoq = (changeNumber) => {
    setLoadingBoq(true);
    fetch(apiUrl(`/api/design-changes/${changeNumber}/boq`))
      .then(res => res.json())
      .then(data => {
        setDcBoqLines(Array.isArray(data) ? data : []);
      })
      .catch(err => {
        console.error('Failed to fetch DC BOQ:', err);
        setDcBoqLines([]);
      })
      .finally(() => setLoadingBoq(false));
  };

  // Load budget line items enriched with current rate-sheet prices. Cached so
  // the Description autocomplete is ready as soon as a DC is clicked.
  const loadBudgetLineItems = useCallback(() => {
    if (!projectId) return;
    setLoadingBudgetItems(true);
    fetch(apiUrl(`/api/budget-line-items?project_id=${projectId}`))
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        return res.json();
      })
      .then(budgetItems => {
        const itemsArr = Array.isArray(budgetItems) ? budgetItems : [];
        return fetch(apiUrl(`/api/rate-sheets?project_id=${projectId}`))
          .then(res => res.json())
          .then(rateSheets => {
            const activeSheet = Array.isArray(rateSheets)
              ? rateSheets.find((rs: any) => rs.is_active) || rateSheets[0]
              : null;
            if (!activeSheet) {
              setBudgetLineItems(itemsArr.map((item: any) => ({ ...item, unit_price: 0 })));
              return;
            }
            return fetch(apiUrl(`/api/rate-sheets/${activeSheet.rate_sheet_id}/items`))
              .then(res => res.json())
              .then(rateItems => {
                const rateArr = Array.isArray(rateItems) ? rateItems : [];
                const enriched = itemsArr.map((item: any) => {
                  const rate = rateArr.find((ri: any) => ri.bli_id === item.bli_id);
                  return { ...item, unit_price: rate ? rate.unit_price : 0 };
                });
                setBudgetLineItems(enriched);
              })
              .catch(() => setBudgetLineItems(itemsArr.map((item: any) => ({ ...item, unit_price: 0 }))));
          })
          .catch(() => setBudgetLineItems(itemsArr.map((item: any) => ({ ...item, unit_price: 0 }))));
      })
      .catch(err => {
        console.error('Failed to fetch budget line items:', err);
        setBudgetLineItems([]);
      })
      .finally(() => setLoadingBudgetItems(false));
  }, [projectId]);

  useEffect(() => {
    if (projectId && access === 'write') loadBudgetLineItems();
  }, [projectId, access, loadBudgetLineItems]);

  // Handle DC row double-click — opens the DC details dialog with BOQ inside
  const handleDCRowDoubleClick = (params) => {
    const dc = params.row;
    setSelectedDC(dc);
    setDcFormData({
      response_required_by: dc.response_required_by ? String(dc.response_required_by).slice(0, 10) : '',
      cost_impact: dc.cost_impact || '',
      qs_status: dc.qs_status || 'Pending',
      qs_remarks: dc.qs_remarks || '',
      cn_number: dc.cn_number || '',
      approved_vo: dc.approved_vo || ''
    });
    setBoqRowModesModel({});
    setSelectedBoqRowId(null);
    pendingBliRef.current = {};
    if (dc?.change_number) {
      fetchDCBoq(dc.change_number);
    }
    setDcDialogOpen(true);
  };

  const handleCloseDCDialog = () => {
    setDcDialogOpen(false);
  };

  const handleSaveDCDialog = async () => {
    if (!selectedDC) return;
    setSavingDC(true);
    try {
      const isoDate = dcFormData.response_required_by || null;
      const response = await fetch(apiUrl(`/api/design-changes/${selectedDC.change_number}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          response_required_by: isoDate,
          cost_impact: dcFormData.cost_impact,
          amount: selectedDC.amount,
          qs_status: dcFormData.qs_status,
          qs_remarks: dcFormData.qs_remarks,
          cn_number: dcFormData.cn_number,
          approved_vo: dcFormData.approved_vo
        })
      });
      if (!response.ok) throw new Error('Update failed');
      await response.json();
      fetchData();
      window.dispatchEvent(new CustomEvent('refresh-pending-counts'));
      setDcDialogOpen(false);
    } catch (err: any) {
      alert(err.message || 'Failed to save design change');
    } finally {
      setSavingDC(false);
    }
  };

  // Inline BOQ row handlers (mirror Material Requisition pattern)
  const getBoqRowKey = (row: any) => row?.dc_boq_line_id ?? row?._id;

  const handleBoqAddRow = () => {
    if (!selectedDC) return;
    const tempId = `temp_${Date.now()}`;
    const nextLineNo = (dcBoqLines || []).reduce(
      (max: number, r: any) => Math.max(max, Number(r.line_no) || 0),
      0
    ) + 1;
    const newRow = {
      _id: tempId,
      dc_boq_line_id: null,
      line_no: nextLineNo,
      bli_id: null,
      item_description: '',
      category_name: '',
      unit_code: '',
      qty: 1,
      unit_price: 0,
      notes: ''
    };
    setDcBoqLines(prev => [...prev, newRow]);
    setBoqRowModesModel(prev => ({
      ...prev,
      [tempId]: { mode: GridRowModes.Edit, fieldToFocus: 'item_description' }
    }));
  };

  const handleBoqEditClick = (id: any) => () => {
    setBoqRowModesModel(prev => ({ ...prev, [id]: { mode: GridRowModes.Edit } }));
  };

  const handleBoqSaveClick = (id: any) => () => {
    setBoqRowModesModel(prev => ({ ...prev, [id]: { mode: GridRowModes.View } }));
  };

  const handleBoqCancelClick = (id: any) => () => {
    setBoqRowModesModel(prev => ({
      ...prev,
      [id]: { mode: GridRowModes.View, ignoreModifications: true }
    }));
    if (String(id).startsWith('temp_')) {
      setDcBoqLines(prev => prev.filter(r => getBoqRowKey(r) !== id));
    }
    delete pendingBliRef.current[id];
    setLiveBoqEdits(prev => {
      const { [String(id)]: _dropped, ...rest } = prev;
      return rest;
    });
  };

  const handleBoqRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  // Persist row on save: POST for new (temp_*) rows, PUT for existing.
  const processBoqRowUpdate = async (newRow: any, oldRow: any) => {
    if (!selectedDC) return oldRow;
    const rowId = getBoqRowKey(newRow);
    const pending = pendingBliRef.current[rowId] || {};
    const merged = { ...newRow, ...pending };
    const isNew = !merged.dc_boq_line_id || String(rowId).startsWith('temp_');

    if (isNew && !merged.bli_id) {
      alert('Please select a Description (Budget Line Item).');
      throw new Error('bli_id required');
    }
    const qtyNum = parseFloat(merged.qty);
    const priceNum = parseFloat(merged.unit_price);
    if (!(qtyNum > 0)) { alert('Quantity must be greater than 0'); throw new Error('qty required'); }
    if (!(priceNum >= 0)) { alert('Unit Price is required'); throw new Error('unit_price required'); }

    try {
      if (isNew) {
        const payload = {
          bli_id: merged.bli_id,
          qty: qtyNum,
          unit_price: priceNum,
          line_no: merged.line_no ? parseInt(merged.line_no) : null,
          notes: merged.notes || '',
          created_by: userId
        };
        const res = await fetch(apiUrl(`/api/design-changes/${selectedDC.change_number}/boq`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-User-ID': String(userId) },
          body: JSON.stringify(payload)
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || 'Failed to add BOQ item');
        }
        const created = await res.json();
        delete pendingBliRef.current[rowId];
        setLiveBoqEdits(prev => {
          const { [String(rowId)]: _dropped, ...rest } = prev;
          return rest;
        });
        fetchDCBoq(selectedDC.change_number);
        fetchData();
        return { ...merged, ...created };
      } else {
        const payload = {
          qty: qtyNum,
          unit_price: priceNum,
          line_no: merged.line_no ? parseInt(merged.line_no) : null,
          notes: merged.notes || ''
        };
        const res = await fetch(apiUrl(`/api/dc-boq-lines/${merged.dc_boq_line_id}`), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'X-User-ID': String(userId) },
          body: JSON.stringify(payload)
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || 'Failed to update BOQ item');
        }
        setLiveBoqEdits(prev => {
          const { [String(rowId)]: _dropped, ...rest } = prev;
          return rest;
        });
        fetchDCBoq(selectedDC.change_number);
        fetchData();
        return merged;
      }
    } catch (e: any) {
      alert(e.message || 'Save failed');
      throw e;
    }
  };

  // Delete BOQ Item - temp rows drop locally, existing rows show confirmation
  const handleDeleteBoqItem = (rowId: any) => {
    if (String(rowId).startsWith('temp_')) {
      setDcBoqLines(prev => prev.filter(r => getBoqRowKey(r) !== rowId));
      setBoqRowModesModel(prev => {
        const { [rowId]: _dropped, ...rest } = prev;
        return rest;
      });
      delete pendingBliRef.current[rowId];
      return;
    }
    setBoqLineToDelete(rowId);
    setDeleteDialogOpen(true);
  };

  // Confirm Delete BOQ Item
  const handleConfirmDeleteBoq = () => {
    if (!boqLineToDelete) return;

    setDeletingBoq(true);
    fetch(apiUrl(`/api/dc-boq-lines/${boqLineToDelete}`), {
      method: 'DELETE',
      headers: { 'X-User-ID': String(userId) }
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to delete BOQ item');
        if (selectedDC) {
          fetchDCBoq(selectedDC.change_number);
          fetchData(); // Refresh to update BOQ value in amount column
        }
        setDeleteDialogOpen(false);
        setBoqLineToDelete(null);
      })
      .catch(err => {
        alert(err.message);
        setDeleteDialogOpen(false);
        setBoqLineToDelete(null);
      })
      .finally(() => setDeletingBoq(false));
  };

  if (access === 'none') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Access denied</Alert>
      </Box>
    );
  }

  const processRowUpdate = updatedRow => {
    const {
      id,
      response_required_by,
      cost_impact,
      amount,
      qs_status,
      qs_remarks,
      cn_number,
      approved_vo
    } = updatedRow;
    const isoDate = response_required_by && typeof response_required_by !== 'string'
      ? new Date(response_required_by).toISOString().slice(0, 10)
      : response_required_by;
    return fetch(apiUrl(`/api/design-changes/${id}`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ response_required_by: isoDate, cost_impact, amount, qs_status, qs_remarks, cn_number, approved_vo })
    }).then(res => {
      if (!res.ok) throw new Error('Update failed');
      return res.json();
    }).then(() => { fetchData(); window.dispatchEvent(new CustomEvent('refresh-pending-counts')); return updatedRow; })
      .catch(err => { alert(err.message); throw err; });
  };

  const handleEditClick = id => () => setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  const handleSaveClick = id => () => setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  const handleCancelClick = id => () => setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View, ignoreModifications: true } });

  const handleDeleteDCClick = (row) => () => {
    if (readOnly) return;
    setDcToDelete(row);
    setDeleteDCDialogOpen(true);
  };

  const handleDeleteDCConfirm = async () => {
    if (!dcToDelete) return;

    setDeletingDC(true);
    try {
      const response = await fetch(apiUrl(`/api/design-changes/${dcToDelete.change_number}`), {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete design change');
      }

      fetchData();
      setDeleteDCDialogOpen(false);
      setDcToDelete(null);
    } catch (error) {
      console.error('Error deleting design change:', error);
      alert(error.message || 'Failed to delete design change');
    } finally {
      setDeletingDC(false);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'change_number',
      headerName: 'DC Number',
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color="primary"
          variant="outlined"
          onClick={(e) => e.stopPropagation()}
          sx={{ fontWeight: 600 }}
        />
      )
    },
    { field: 'change_name', headerName: 'Name', flex: 1, minWidth: 200 },
    { field: 'affected_systems', headerName: 'Systems', flex: 1, minWidth: 150 },
    {
      field: 'change_date_issued',
      headerName: 'Issued Date',
      width: 120,
      valueGetter: (p: any) => (p && p.value ? new Date(p.value).toLocaleDateString('en-GB') : '')
    },
    {
      field: 'response_required_by',
      headerName: 'Response By',
      width: 130,
      editable: true,
      renderEditCell: params => (
        <TextField
          type="date"
          size="small"
          value={params.value ? String(params.value).slice(0, 10) : ''}
          onChange={e => params.api.setEditCellValue({ id: params.id, field: params.field, value: e.target.value }, e)}
          InputLabelProps={{ shrink: true }}
        />
      )
    },
    {
      field: 'cost_impact',
      headerName: 'Cost Impact',
      width: 130,
      editable: true,
      type: 'singleSelect',
      valueOptions: ['Yes', 'No'],
      renderCell: (params) => {
        if (params.value === 'Yes') {
          return <Chip label="Yes" size="small" color="warning" onClick={(e) => e.stopPropagation()} />;
        } else if (params.value === 'No') {
          return <Chip label="No" size="small" color="success" onClick={(e) => e.stopPropagation()} />;
        }
        return params.value;
      }
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 160,
      editable: false,
      type: 'number',
      valueGetter: (value, row) => {
        const total = row.boq_value || 0;
        return formatCurrency(total, 'SGD', companyData.number_format);
      },
      renderCell: (params) => <Typography>{params.value}</Typography>
    },
    {
      field: 'remaining_balance',
      headerName: 'Remaining Balance',
      width: 160,
      editable: false,
      type: 'number',
      valueGetter: (value, row) => {
        const balance = row.remaining_balance ?? 0;
        return formatCurrency(balance, 'SGD', companyData.number_format);
      },
      renderCell: (params) => <Typography>{params.value}</Typography>
    },
    {
      field: 'qs_status',
      headerName: 'QS Status',
      width: 130,
      editable: true,
      type: 'singleSelect',
      valueOptions: ['Pending', 'Submitted', 'Approved'],
      renderCell: (params) => {
        const statusColors = {
          'Pending': 'warning',
          'Submitted': 'info',
          'Approved': 'success'
        };
        return (
          <Chip
            label={params.value || 'Pending'}
            size="small"
            color={statusColors[params.value] || 'default'}
            onClick={(e) => e.stopPropagation()}
          />
        );
      }
    },
    { field: 'qs_remarks', headerName: 'Remarks', flex: 1, minWidth: 150, editable: true },
    { field: 'cn_number', headerName: 'CN Number', width: 130, editable: true },
    { field: 'approved_vo', headerName: 'Approved VO', width: 130, editable: true },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 120,
      getActions: ({ id, row }) => {
        const isEditing = rowModesModel[id]?.mode === GridRowModes.Edit;
        return isEditing
          ? [
              <GridActionsCellItem
                icon={<Tooltip title="Save"><SaveIcon /></Tooltip>}
                label="Save"
                onClick={handleSaveClick(id)}
                showInMenu={false}
                key="save"
              />,
              <GridActionsCellItem
                icon={<Tooltip title="Cancel"><CancelIcon /></Tooltip>}
                label="Cancel"
                onClick={handleCancelClick(id)}
                showInMenu={false}
                key="cancel"
              />
            ]
          : [
              ...(readOnly ? [] : [
                <GridActionsCellItem
                  icon={<Tooltip title="Edit"><EditIcon /></Tooltip>}
                  label="Edit"
                  onClick={handleEditClick(id)}
                  showInMenu={false}
                  key="edit"
                />,
                <GridActionsCellItem
                  icon={<Tooltip title="Delete"><DeleteIcon /></Tooltip>}
                  label="Delete"
                  onClick={handleDeleteDCClick(row)}
                  showInMenu={false}
                  key="delete"
                />
              ])
            ];
      }
    }
  ];

  if (!projectId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Please select a project to continue.</Alert>
      </Box>
    );
  }

  // Number edit cell that mirrors edits into `liveBoqEdits` so the Line Total
  // cell can show the new total on every keystroke (DataGrid only updates the
  // row data on row save).
  const BoqNumberEditCell = (
    params: GridRenderEditCellParams & { fieldName: 'qty' | 'unit_price' }
  ) => {
    const { fieldName } = params;
    return (
      <TextField
        type="number"
        defaultValue={params.value ?? ''}
        autoFocus={params.hasFocus}
        variant="standard"
        inputProps={{ step: '0.01', style: { textAlign: 'right' } }}
        onChange={(e) => {
          const raw = e.target.value;
          const num = raw === '' ? 0 : Number(raw);
          params.api.setEditCellValue({ id: params.id, field: fieldName, value: raw === '' ? '' : num });
          setLiveBoqEdits(prev => ({
            ...prev,
            [String(params.id)]: { ...prev[String(params.id)], [fieldName]: Number.isFinite(num) ? num : 0 }
          }));
        }}
        sx={{ width: '100%', px: 1, '& .MuiInput-root': { fontSize: '0.875rem' } }}
      />
    );
  };

  // Description edit cell: Autocomplete over budget line items. On selection we
  // commit the editable fields through DataGrid's edit state AND stash the
  // derived/read-only fields (bli_id, category_name, unit_code) in pendingBliRef
  // so they survive to processBoqRowUpdate.
  const BoqDescriptionEditCell = (params: GridRenderEditCellParams) => (
    <Autocomplete
      fullWidth
      size="small"
      loading={loadingBudgetItems}
      options={budgetLineItems}
      getOptionLabel={(option: any) => (typeof option === 'string' ? option : option.item_description || '')}
      value={budgetLineItems.find((item: any) => item.item_description === params.value) || null}
      isOptionEqualToValue={(opt: any, val: any) => opt?.bli_id === val?.bli_id}
      noOptionsText={loadingBudgetItems ? 'Loading...' : (budgetLineItems.length === 0 ? 'No budget line items available' : 'No matching items found')}
      onChange={(_event, newValue: any) => {
        if (newValue && typeof newValue !== 'string') {
          // bli_id has no column of its own, so it rides in pendingBliRef until save.
          pendingBliRef.current[params.id as any] = { bli_id: newValue.bli_id };
          const newPrice = Number(newValue.unit_price) || 0;
          params.api.setEditCellValue({ id: params.id, field: 'item_description', value: newValue.item_description || '' });
          params.api.setEditCellValue({ id: params.id, field: 'unit_price', value: newPrice });
          // Push into the grid's edit state so the Category/UoM display-only cells refresh immediately.
          params.api.setEditCellValue({ id: params.id, field: 'category_name', value: newValue.category_name || '' });
          params.api.setEditCellValue({ id: params.id, field: 'unit_code', value: newValue.unit_code || '' });
          // Mirror the auto-filled price into liveBoqEdits so Line Total updates immediately.
          setLiveBoqEdits(prev => ({
            ...prev,
            [String(params.id)]: { ...prev[String(params.id)], unit_price: newPrice }
          }));
        } else {
          delete pendingBliRef.current[params.id as any];
          params.api.setEditCellValue({ id: params.id, field: 'item_description', value: '' });
          params.api.setEditCellValue({ id: params.id, field: 'category_name', value: '' });
          params.api.setEditCellValue({ id: params.id, field: 'unit_code', value: '' });
        }
      }}
      renderOption={(props, option: any) => {
        const { key, ...otherProps } = props as any;
        return (
          <Box component="li" key={key} {...otherProps} sx={{ display: 'block', py: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {option.item_description}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {option.category_name} • {option.unit_code} • ${option.unit_price || 0}
            </Typography>
          </Box>
        );
      }}
      filterOptions={(options, { inputValue }) => options.filter((option: any) =>
        option.item_description?.toLowerCase().includes(inputValue.toLowerCase()) ||
        option.category_name?.toLowerCase().includes(inputValue.toLowerCase())
      )}
      renderInput={(inputParams) => (
        <TextField
          {...inputParams}
          variant="standard"
          placeholder="Search and select item..."
          sx={{ '& .MuiInput-root': { fontSize: '0.875rem' } }}
        />
      )}
      sx={{ width: '100%' }}
    />
  );

  const boqColumns: GridColDef[] = [
    {
      field: 'line_no',
      headerName: 'Line #',
      width: 90,
      type: 'number',
      editable: !readOnly,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => (
        <Chip label={params.value || '-'} size="small" variant="outlined" />
      )
    },
    {
      field: 'item_description',
      headerName: 'Description',
      flex: 1,
      minWidth: 250,
      editable: !readOnly,
      renderEditCell: (params) => {
        // BLI cannot change on existing rows (backend PUT ignores bli_id), so show
        // plain text instead of the Autocomplete once the row has been saved.
        const isNewRow = String(params.id).startsWith('temp_');
        if (!isNewRow) {
          return (
            <Typography sx={{ fontSize: 13, px: 1 }}>
              {params.value || '-'}
            </Typography>
          );
        }
        return <BoqDescriptionEditCell {...params} />;
      }
    },
    {
      field: 'category_name',
      headerName: 'Category',
      width: 140,
      // Must be editable so the Description Autocomplete's setEditCellValue can
      // push a fresh value into the staged edit row. The edit cell just renders
      // the same read-only display as view mode.
      editable: !readOnly,
      renderCell: (params) => (
        <Typography sx={{ fontSize: 13, color: '#555' }}>{params.value || '-'}</Typography>
      ),
      renderEditCell: (params) => (
        <Typography sx={{ fontSize: 13, color: '#555', px: 1 }}>{params.value || '-'}</Typography>
      )
    },
    {
      field: 'unit_code',
      headerName: 'UoM',
      width: 80,
      editable: !readOnly,
      renderCell: (params) => (
        <Typography sx={{ fontSize: 13, color: '#555' }}>{params.value || '-'}</Typography>
      ),
      renderEditCell: (params) => (
        <Typography sx={{ fontSize: 13, color: '#555', px: 1 }}>{params.value || '-'}</Typography>
      )
    },
    {
      field: 'qty',
      headerName: 'Quantity',
      width: 110,
      type: 'number',
      editable: !readOnly,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (value: any) => {
        if (value == null || value === '') return '';
        return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(value));
      },
      renderEditCell: (params) => (
        <BoqNumberEditCell {...params} fieldName="qty" />
      )
    },
    {
      field: 'unit_price',
      headerName: 'Unit Price',
      width: 130,
      type: 'number',
      editable: !readOnly,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Typography sx={{ fontSize: 13 }}>{formatCurrency(Number(params.value) || 0, 'SGD', companyData.number_format)}</Typography>
      ),
      renderEditCell: (params) => (
        <BoqNumberEditCell {...params} fieldName="unit_price" />
      )
    },
    {
      field: 'line_total',
      headerName: 'Line Total',
      width: 150,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      // Read staged edits from `liveBoqEdits` so the total updates on every
      // keystroke; fall back to the saved row values when the row isn't being edited.
      renderCell: (params) => {
        const live = liveBoqEdits[String(params.id)];
        const qty = live && live.qty !== undefined ? live.qty : params.row.qty;
        const price = live && live.unit_price !== undefined ? live.unit_price : params.row.unit_price;
        const total = (Number(qty) || 0) * (Number(price) || 0);
        return (
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
            {formatCurrency(total, 'SGD', companyData.number_format)}
          </Typography>
        );
      }
    },
    {
      field: 'notes',
      headerName: 'Notes',
      flex: 1,
      minWidth: 150,
      editable: !readOnly,
      renderCell: (params) => (
        <Typography sx={{ fontSize: 13, color: params.value ? '#222' : '#bbb', fontStyle: params.value ? 'normal' : 'italic' }}>
          {params.value || '-'}
        </Typography>
      )
    },
    ...(readOnly ? [] : [{
      field: 'actions',
      type: 'actions' as const,
      headerName: 'Actions',
      width: 110,
      getActions: ({ id }: { id: any }) => {
        const isInEditMode = boqRowModesModel[id]?.mode === GridRowModes.Edit;
        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<Tooltip title="Save"><SaveIcon /></Tooltip>}
              label="Save"
              color="primary"
              onClick={handleBoqSaveClick(id)}
              key="save"
            />,
            <GridActionsCellItem
              icon={<Tooltip title="Cancel"><CancelIcon /></Tooltip>}
              label="Cancel"
              onClick={handleBoqCancelClick(id)}
              color="inherit"
              key="cancel"
            />
          ];
        }
        return [
          <GridActionsCellItem
            icon={<Tooltip title="Edit"><EditIcon /></Tooltip>}
            label="Edit"
            onClick={handleBoqEditClick(id)}
            color="inherit"
            key="edit"
          />,
          <GridActionsCellItem
            icon={<Tooltip title="Delete"><DeleteIcon /></Tooltip>}
            label="Delete"
            onClick={() => handleDeleteBoqItem(id)}
            color="inherit"
            key="delete"
          />
        ];
      }
    }])
  ];

  // Rows need a stable id whether saved (dc_boq_line_id) or unsaved (temp _id).
  const boqRows = (dcBoqLines || []).map((r: any) => ({ ...r, id: getBoqRowKey(r) }));

  return (
    <Box sx={{ width: '100%' }}>

      {/* Design Changes Section */}
      <Box sx={{ margin: '0 24px 24px 24px' }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Double-click any design change row to view full details and manage its budget line items
        </Alert>
        <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden', background: 'white' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={r => r.id}
            editMode="row"
            rowModesModel={rowModesModel}
            onRowModesModelChange={setRowModesModel}
            processRowUpdate={processRowUpdate}
            onRowClick={(params) => setSelectedDCRowId(params.id)}
            onRowDoubleClick={handleDCRowDoubleClick}
            loading={loading}
            autoHeight
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
            }}
            rowSelectionModel={
              selectedDCRowId != null
                ? { type: 'include', ids: new Set([selectedDCRowId]) }
                : { type: 'include', ids: new Set() }
            }
            sx={{ ...dataGridSx, '& .MuiDataGrid-row': { ...dataGridSx['& .MuiDataGrid-row'], cursor: 'pointer' } }}
          />
        </Box>
      </Box>

      {/* Design Change Details Dialog (DC info + BOQ in one popup) */}
      <Dialog
        open={dcDialogOpen}
        onClose={handleCloseDCDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 2 }}>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Design Change Details
            </Typography>
            {selectedDC && (
              <Typography variant="body2" color="text.secondary">
                {selectedDC.change_number}{selectedDC.change_name ? ` — ${selectedDC.change_name}` : ''}
              </Typography>
            )}
          </Box>
          {selectedDC?.qs_status && (
            <Chip
              label={selectedDC.qs_status}
              size="small"
              color={selectedDC.qs_status === 'Approved' ? 'success' : selectedDC.qs_status === 'Submitted' ? 'info' : 'warning'}
            />
          )}
        </DialogTitle>
        <DialogContent dividers>
          {selectedDC && (
            <Box>
              {/* General Info (read-only) */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>General Information</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">DC Number</Typography>
                    <Typography variant="body2" fontWeight={600}>{selectedDC.change_number}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Change Name</Typography>
                    <Typography variant="body2" fontWeight={600}>{selectedDC.change_name || '-'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Affected Systems</Typography>
                    <Typography variant="body2">{selectedDC.affected_systems || '-'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Issued Date</Typography>
                    <Typography variant="body2">{selectedDC.change_date_issued ? new Date(selectedDC.change_date_issued).toLocaleDateString('en-GB') : '-'}</Typography>
                  </Box>
                </Box>
              </Box>

              {/* Editable QS Fields */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>QS Details</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <TextField
                    label="Response Required By"
                    type="date"
                    size="small"
                    fullWidth
                    value={dcFormData.response_required_by}
                    onChange={(e) => setDcFormData(prev => ({ ...prev, response_required_by: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    disabled={readOnly}
                  />
                  <TextField
                    label="Cost Impact"
                    select
                    size="small"
                    fullWidth
                    value={dcFormData.cost_impact}
                    onChange={(e) => setDcFormData(prev => ({ ...prev, cost_impact: e.target.value }))}
                    disabled={readOnly}
                  >
                    <MenuItem value="">-</MenuItem>
                    <MenuItem value="Yes">Yes</MenuItem>
                    <MenuItem value="No">No</MenuItem>
                  </TextField>
                  <TextField
                    label="QS Status"
                    select
                    size="small"
                    fullWidth
                    value={dcFormData.qs_status}
                    onChange={(e) => setDcFormData(prev => ({ ...prev, qs_status: e.target.value }))}
                    disabled={readOnly}
                  >
                    <MenuItem value="Pending">Pending</MenuItem>
                    <MenuItem value="Submitted">Submitted</MenuItem>
                    <MenuItem value="Approved">Approved</MenuItem>
                  </TextField>
                  <TextField
                    label="Approved VO"
                    size="small"
                    fullWidth
                    value={dcFormData.approved_vo}
                    onChange={(e) => setDcFormData(prev => ({ ...prev, approved_vo: e.target.value }))}
                    disabled={readOnly}
                  />
                  <TextField
                    label="CN Number"
                    size="small"
                    fullWidth
                    value={dcFormData.cn_number}
                    onChange={(e) => setDcFormData(prev => ({ ...prev, cn_number: e.target.value }))}
                    disabled={readOnly}
                  />
                  <Box />
                  <TextField
                    label="QS Remarks"
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    value={dcFormData.qs_remarks}
                    onChange={(e) => setDcFormData(prev => ({ ...prev, qs_remarks: e.target.value }))}
                    disabled={readOnly}
                    sx={{ gridColumn: '1 / -1' }}
                  />
                </Box>
              </Box>

              {/* Totals */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Amount (from BOQ)</Typography>
                    <Typography variant="body1" fontWeight={600}>{formatCurrency(selectedDC.boq_value || 0, 'SGD', companyData.number_format)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Remaining Balance</Typography>
                    <Typography variant="body1" fontWeight={600}>{formatCurrency(selectedDC.remaining_balance ?? 0, 'SGD', companyData.number_format)}</Typography>
                  </Box>
                </Box>
              </Box>

              {/* BOQ Section */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, mt: 1 }}>
                <Typography variant="subtitle2" fontWeight={600}>Budget Line Items</Typography>
                {!readOnly && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleBoqAddRow}
                  >
                    Add Budget Item
                  </Button>
                )}
              </Box>
              <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden', background: 'white' }}>
                <DataGrid
                  rows={boqRows}
                  columns={boqColumns}
                  getRowId={(row) => row.id}
                  editMode="row"
                  rowModesModel={boqRowModesModel}
                  onRowModesModelChange={setBoqRowModesModel}
                  onRowEditStop={handleBoqRowEditStop}
                  processRowUpdate={processBoqRowUpdate}
                  onProcessRowUpdateError={(err) => console.error('BOQ row update failed:', err)}
                  loading={loadingBoq}
                  autoHeight
                  pageSizeOptions={[5, 10, 25]}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 10 } },
                  }}
                  onRowClick={(params) => setSelectedBoqRowId(params.id)}
                  rowSelectionModel={
                    selectedBoqRowId != null
                      ? { type: 'include', ids: new Set([selectedBoqRowId]) }
                      : { type: 'include', ids: new Set() }
                  }
                  sx={{ ...dataGridSx, '& .MuiDataGrid-row': { ...dataGridSx['& .MuiDataGrid-row'], cursor: 'pointer' } }}
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDCDialog} disabled={savingDC} variant="outlined">
            Close
          </Button>
          {!readOnly && (
            <Button
              onClick={handleSaveDCDialog}
              variant="contained"
              disabled={savingDC}
              startIcon={savingDC ? <CircularProgress size={18} color="inherit" /> : undefined}
            >
              {savingDC ? 'Saving...' : 'Save'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete DC Confirmation Dialog */}
      <Dialog
        open={deleteDCDialogOpen}
        onClose={() => !deletingDC && setDeleteDCDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>
            Confirm Delete Design Change
          </Typography>
        </DialogTitle>
        <DialogContent>
          {dcToDelete && (
            <>
              <Typography variant="body1">
                Are you sure you want to delete Design Change <strong>{dcToDelete.change_number}</strong>?
              </Typography>
              {dcToDelete.change_name && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {dcToDelete.change_name}
                </Typography>
              )}
              <Typography variant="body2" color="error" sx={{ mt: 2, fontWeight: 600 }}>
                ⚠️ This will also delete all associated:
              </Typography>
              <Typography component="ul" variant="body2" color="error" sx={{ mt: 1, pl: 2 }}>
                <li>BOQ Items</li>
                <li>Material Requisitions (MRs)</li>
                <li>Purchase Orders (POs)</li>
                <li>Assignments</li>
                <li>Documents</li>
              </Typography>
              <Typography variant="body2" color="error" sx={{ mt: 1, fontWeight: 600 }}>
                This action cannot be undone.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setDeleteDCDialogOpen(false)}
            disabled={deletingDC}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteDCConfirm}
            variant="contained"
            color="error"
            disabled={deletingDC}
            startIcon={deletingDC ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
          >
            {deletingDC ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete BOQ Item Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deletingBoq && setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>
            Confirm Delete
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete this BOQ item?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deletingBoq}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeleteBoq}
            variant="contained"
            color="error"
            disabled={deletingBoq}
            startIcon={deletingBoq ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
          >
            {deletingBoq ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QSTeam;
